import { Field, FormikProps, FormikProvider } from "formik";
import { TextInputFormik } from "../../../../../formcontrols/InputItem";
import { PersonDto } from "../../../../../../interfaces/schema";
import AutoCompleteFormControl from "../../../../../formcontrols/AutoCompleteFormControl";
import Button from "@/components/bricks/button/Button";
import VisibilityIcon from "../../../../../icons/VisibilityIcon";
import {
  calculateAge,
  dateToyyyyMMdd,
  stringDateToPP,
  stringDateToPPP,
  stringDateToddMMyy,
} from "../../../../../../util/date";
import OpretPersonIcon from "../../../../../icons/OpretPersonIcon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../../../select/Select";
import { FormikDatePicker } from "../../../../../formcontrols/DatePicker";
import {
  extractCurrentPrincipal,
  extractMoedeRaetter,
} from "../../../../../../util/person";
import { useHardcodedQuery } from "../../../../../../hooks/useQueries";
import { titlerHardKodet } from "../../../../../../hooks/titlerHardkodet";
import { Link } from "react-router-dom";
import ValidationErrorMessage from "../../../../../formcontrols/ValidationErrorMessage";
import VisibilityOffIcon from "../../../../../icons/VisibilityOffIcon";
import { DisplayItem } from "../../../../../formcontrols/DisplayItem";
import { defaultIfNullish } from "../../../../../../util/stringUtil";
import { AlertMessage } from "../../../../../AlertMessage";
import { useConfirm } from "../../../../../confirm/useConfirm";
import {
  cprAdgangInfo,
  canEditFoedselsDato,
} from "../../../../../../util/cprAccess";

const PersonStamoFieldset1 = ({
  editMode,
  formik,
  isLoading,
  person,
  toggleCpr: toggleCprProp,
  setToggleCpr,
  canReadCpr,
}: {
  formik: FormikProps<IUpdatePersonFormik>;
  isLoading: boolean;
  editMode: boolean;
  person: PersonDto;
  toggleCpr: boolean;
  setToggleCpr: React.Dispatch<React.SetStateAction<boolean>>;
  canReadCpr: boolean;
}) => {
  const { efternavn, fornavne, personDoedDato } = person;
  const { values } = formik;

  const { confirmDialog } = useConfirm();
  // ADV-349: uden CprRead vises løbenummeret aldrig, uanset delt toggle-state.
  const toggleCpr = canReadCpr && toggleCprProp;
  const foedselsDatoLaast = !canEditFoedselsDato(canReadCpr, {
    harCprNummer: !!person.harCprNummer,
    foedselsDato: person.foedselsDato,
  });

  const møderætter = extractMoedeRaetter(person);
  const currPrincipal = extractCurrentPrincipal(person);
  const initialQueryValues = useHardcodedQuery<ITitel>(titlerHardKodet);
  const queryValues = {
    ...initialQueryValues,
    data: initialQueryValues.data?.map((titel) => {
      return {
        ...titel,
        id: titel.Id,
        navn: titel.Navn,
      };
    }),
  };

  return (
    <fieldset
      className="col-12 col-xl-6 d-flex flex-column gap-4"
      disabled={isLoading}
    >
      <div className="c-card c-item-list">
        {editMode && (
          <div>
            <TextInputFormik
              config={{
                component: "textinput",
                id: "Fornavne",
                initialValue: formik.initialValues.Fornavne ?? "",
                label: "Fornavne",
                placeholder: "Indtast fornavne",
              }}
              formik={formik}
            />
          </div>
        )}
        {!editMode && (
          <div>
            <h5 className="form-label">Fornavne</h5>
            <div className="form-control--value is-editable">{fornavne}</div>
          </div>
        )}
        {editMode && (
          <div>
            <TextInputFormik
              config={{
                component: "textinput",
                id: "Efternavn",
                initialValue: formik.initialValues.Efternavn ?? "",
                label: "Efternavn",
                placeholder: "Indtast efternavn",
              }}
              formik={formik}
            />
          </div>
        )}
        {!editMode && (
          <div>
            <h5 className="form-label">Efternavn</h5>
            <div className="form-control--value is-editable">{efternavn}</div>
          </div>
        )}
        {editMode && (
          <div>
            <h5 className="form-label">Titel 1</h5>
            <AutoCompleteFormControl
              onChange={(id) => {
                formik.setFieldValue("TitelId", id);
              }}
              value={formik.values.TitelId}
              queryValues={queryValues}
            />
          </div>
        )}
        {!editMode && (
          <div>
            <h5 className="form-label">Titel 1</h5>
            <div className="form-control--value is-editable">
              {person.titel ? person.titel.navn : "-"}
            </div>
          </div>
        )}
        {editMode && (
          <div>
            <h5 className="form-label">Titel 2</h5>
            <AutoCompleteFormControl
              onChange={(id) => {
                formik.setFieldValue("Titel1Id", id);
              }}
              value={formik.values.Titel1Id}
              queryValues={queryValues}
            />
          </div>
        )}
        {!editMode && (
          <div>
            <h5 className="form-label">Titel 2</h5>
            <div className="form-control--value is-editable">
              {person.titel1 ? (
                <span>{person.titel1.navn}</span>
              ) : (
                <span className="text-muted">-</span>
              )}
            </div>
          </div>
        )}
        {editMode && (
          <>
            <div>
              <label className="form-label" htmlFor="cpr-input">
                {" "}
                {canReadCpr ? "Fødselsdato / CPR-nr." : "Fødselsdato"}
              </label>
              <div className="d-flex align-items-start gap-2 w-100">
                <div className="flex-grow-1">
                  <FormikDatePicker
                    placeholder="dd.mm.åååå"
                    formik={formik}
                    disabled={foedselsDatoLaast}
                    config={{
                      component: "datepicker",
                      id: "FoedselsDato",
                      label: "",
                      initialValue: formik.initialValues.FoedselsDato ?? "",
                    }}
                  />
                </div>
                {canReadCpr && (
                  <>
                    <span className="mt-2">-</span>
                    <div className="input-group flex-grow-1">
                      <FormikProvider value={formik}>
                        <Field
                          autoComplete="off"
                          name="CprLoebeNr"
                          placeholder={"****"}
                          style={{ width: "65px" }}
                          className="form-control"
                        />
                      </FormikProvider>
                    </div>
                  </>
                )}
              </div>
              {cprAdgangInfo(canReadCpr, {
                harCprNummer: !!person.harCprNummer,
                foedselsDato: person.foedselsDato,
              }) && (
                <AlertMessage type="info" className="mt-2">
                  {cprAdgangInfo(canReadCpr, {
                    harCprNummer: !!person.harCprNummer,
                    foedselsDato: person.foedselsDato,
                  })}
                </AlertMessage>
              )}
              {canReadCpr && (
                <ValidationErrorMessage
                  touched={formik.touched.CprLoebeNr}
                  errorMessage={formik.errors.CprLoebeNr}
                />
              )}
            </div>
            <div></div>
          </>
        )}
        {!editMode &&
        person.foedselsDato &&
        (values.CprLoebeNr?.length === 4 || !toggleCpr) ? (
          <>
            <div>
              <h5 className="form-label">
                {canReadCpr ? "Fødselsdato / CPR-nr." : "Fødselsdato"}
              </h5>
              <div className="form-control--value is-editable d-flex align-items-center">
                <span className="me-1">
                  {toggleCpr &&
                    values.CprLoebeNr &&
                    `${
                      person.foedselsDato
                        ? stringDateToddMMyy(person.foedselsDato)
                        : ""
                    }-${values.CprLoebeNr}`}
                  {toggleCpr &&
                    !values.CprLoebeNr &&
                    `${
                      person.foedselsDato
                        ? stringDateToddMMyy(person.foedselsDato)
                        : ""
                    }`}
                  {!toggleCpr &&
                    `${
                      person.foedselsDato
                        ? stringDateToddMMyy(person.foedselsDato)
                        : ""
                    }${person.harCprNummer ? "-****" : ""}`}
                </span>
                {canReadCpr && person.harCprNummer && (
                  <Button
                    type="button"
                    aria-label={
                      toggleCpr ? "Skjul cpr-nummer" : "Vis cpr-nummer"
                    }
                    color="secondary"
                    size="sm"
                    className="btn--icon w-auto h-auto p-1 ms-auto my-n1 me-n2"
                    onClick={() => setToggleCpr(!toggleCpr)}
                  >
                    {!toggleCpr && <VisibilityIcon />}
                    {toggleCpr && <VisibilityOffIcon />}
                  </Button>
                )}
              </div>
            </div>
            <div>
              <h5 className="form-label">Alder</h5>
              <div className="form-control--value is-airy">
                {stringDateToPP(person.personDoedDato ?? null) === "-" &&
                  values.FoedselsDato &&
                  calculateAge(dateToyyyyMMdd(values.FoedselsDato)) + " år"}
              </div>
            </div>
          </>
        ) : !editMode ? (
          <>
            <div>
              <DisplayItem
                label={canReadCpr ? "Fødselsdato / CPR-nr" : "Fødselsdato"}
                text={defaultIfNullish(
                  person.foedselsDato
                    ? stringDateToddMMyy(person.foedselsDato)
                    : null,
                )}
                className="form-label"
                isEditable
              />
            </div>
            <div>
              <h5 className="form-label">Alder</h5>
              <div className="form-control--value is-airy">
                {stringDateToPP(person.personDoedDato ?? null) === "-" &&
                  values.FoedselsDato &&
                  calculateAge(dateToyyyyMMdd(values.FoedselsDato)) + " år"}
              </div>
            </div>
          </>
        ) : null}
        <div>
          <h5 className="form-label">Dødsdato</h5>
          <div className="form-control--value d-flex gap-3 align-items-center w-100 justify-content-between">
            {personDoedDato && (
              <>
                <span>{stringDateToPPP(personDoedDato)}</span>
                <Button
                  type="button"
                  className="btn--action"
                  color="transparent"
                  onClick={async () => {
                    try {
                      await confirmDialog(
                        "Genopliv person",
                        `Kontakt Systemadministrator for at få bistand til at genoprette data for en person, som er Dødsmarkeret ved en fejl. 
                      Data skal genskabes af udvikler direkte i databasen.`,
                        "Luk",
                      );
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    } catch (e) {
                      //
                    }
                  }}
                >
                  <OpretPersonIcon height={16} /> Genopliv ...
                </Button>
                {(personDoedDato === "1753-01-01T00:00:00.000+00:54" ||
                  stringDateToPP(personDoedDato ?? null) === "-") && (
                  <span className="text-muted">-</span>
                )}
              </>
            )}
          </div>
        </div>
        {editMode && (
          <div>
            <label className="form-label">Køn</label>
            <Select
              value={formik.values.Koen}
              onValueChange={(value) => {
                if (value === "__clear__") {
                  formik.setFieldValue("Koen", "");
                  return;
                }
                formik.setFieldValue("Koen", value);
              }}
            >
              <SelectTrigger aria-label="Køn" placeholder="Vælg..." />
              <SelectContent>
                <SelectItem value="__clear__"> </SelectItem>
                <SelectItem value="K">Kvinde</SelectItem>
                <SelectItem value="M">Mand</SelectItem>
                <SelectItem value="N">Nonbinær</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {!editMode && (
          <div>
            <h5 className="form-label">Køn</h5>
            <div className="form-control--value is-editable">
              {(() => {
                switch (person.koen) {
                  case "M":
                    return "Mand";
                  case "K":
                    return "Kvinde";
                  case "N":
                    return "Nonbinær";
                  default:
                    return "";
                }
              })()}
            </div>
          </div>
        )}
      </div>
      <div className="c-card c-item-list">
        <div>
          <h5 className="form-label">Møderet (fra dato)</h5>

          {møderætter &&
            møderætter.length > 0 &&
            møderætter.map((m) => {
              return (
                <div className="form-control--value" key={m.id}>
                  {m.persontypeNrNavigation?.navn.replace("Møderet for ", "")},{" "}
                  {stringDateToPP(m.periode.fraDato)}
                </div>
              );
            })}
          {(!møderætter || møderætter.length === 0) && (
            <div className="form-control--value">
              <span className="text-muted">-</span>
            </div>
          )}
        </div>
        <div>
          <h5 className="form-label">Principalnavn</h5>{" "}
          {currPrincipal && (
            <Link
              className="form-control--value link-primary"
              to={`/person/${currPrincipal.personPrincipalNavigation.id}`}
            >
              {currPrincipal.personPrincipalNavigation.fuldtNavn}
            </Link>
          )}
          {!currPrincipal && (
            <div className="form-control--value">
              <span className="text-muted">-</span>
            </div>
          )}
        </div>
      </div>
    </fieldset>
  );
};

export default PersonStamoFieldset1;
